import React, { useEffect, useState } from "react";
import { getAlert, insertData, getData } from "../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  TimeUnit_Code: "",
  TimeUnit_Name: "",
  TimeUnit_Type: "CONTRACT",
  TimeUnit_Moment: "months",
  TimeUnit_Sort: "",
  TimeUnit_Status: "Y",
};

const TimeUnitQuickModal = ({ open, onClose, defaultType = "CONTRACT", onSaved }) => {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    let isMounted = true;

    const normalizeList = (res) => {
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.data)) return res.data;
      return [];
    };

    const fetchNextCode = async () => {
      try {
        const res = await getData(
          "Tb_Set_TimeUnit",
          "1=1 ORDER BY TimeUnit_Code DESC"
        );
        const list = normalizeList(res);
        const maxCode = list[0]?.TimeUnit_Code;
        const nextCode = Number(maxCode || 0) + 1;

        if (!isMounted) return;
        setFormData((prev) => ({
          ...DEFAULT_FORM,
          TimeUnit_Code: nextCode ? String(nextCode) : "",
          TimeUnit_Type: defaultType,
          TimeUnit_Moment: defaultType === "SERVICE" ? "" : prev.TimeUnit_Moment,
        }));
      } catch (error) {
        console.error("Error fetching time unit code:", error);
        if (!isMounted) return;
        setFormData((prev) => ({
          ...DEFAULT_FORM,
          TimeUnit_Type: defaultType,
          TimeUnit_Moment: defaultType === "SERVICE" ? "" : prev.TimeUnit_Moment,
        }));
      }
    };

    fetchNextCode();
    return () => {
      isMounted = false;
    };
  }, [open, defaultType]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "TimeUnit_Type" && value === "SERVICE") {
        next.TimeUnit_Moment = "";
      }
      if (name === "TimeUnit_Type" && value === "CONTRACT" && !next.TimeUnit_Moment) {
        next.TimeUnit_Moment = "months";
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!formData.TimeUnit_Code || !formData.TimeUnit_Name || !formData.TimeUnit_Type) {
      getAlert("WARNING", "โปรดกรอกข้อมูลให้ครบ");
      return;
    }
    try {
      setIsSaving(true);
      const fields = Object.keys(formData).join(", ");
      const values = Object.values(formData);
      await insertData("Tb_Set_TimeUnit", fields, values);
      getAlert("OK", "เพิ่มหน่วยสำเร็จ");
      await onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Error inserting time unit:", error);
      getAlert("FAILED", "ไม่สามารถบันทึกข้อมูลได้");
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  const isServiceType = formData.TimeUnit_Type === "SERVICE";

  return (
    <>
      <div
        className={`modal ${open ? "show" : ""}`}
        style={{ display: open ? "block" : "none", zIndex: 1060 }}
        tabIndex="-1"
        role="dialog"
      >
        <div
          className="modal-dialog modal-dialog-centered"
          role="document"
          style={{ maxWidth: "640px", width: "95%" }}
        >
          <div
            className="modal-content"
            style={{
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 10px 35px rgba(0,0,0,0.25)",
              background: "#ffffff",
            }}
          >
            <div
              className="modal-header"
              style={{ background: "#ffffff", borderBottom: "1px solid #eef0f3" }}
            >
              <h5 className="modal-title" style={{ fontWeight: 600, margin: 0 }}>
                เพิ่มหน่วยช่วงเวลา
              </h5>
              <button
                type="button"
                className="close"
                onClick={onClose}
                aria-label="Close"
                style={{ fontSize: "1.25rem", lineHeight: 1 }}
                disabled={isSaving}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-body" style={{ background: "#fff" }}>
              <div className="row g-3">
                <div className="col-md-12">
                  <label className="form-label">ชื่อหน่วย</label>
                  <input
                    type="text"
                    className="form-control"
                    name="TimeUnit_Name"
                    value={formData.TimeUnit_Name || ""}
                    onChange={handleChange}
                    placeholder="เช่น เดือน / ครั้ง"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">ประเภท</label>
                  <select
                    className="form-select"
                    name="TimeUnit_Type"
                    value={formData.TimeUnit_Type || "CONTRACT"}
                    onChange={handleChange}
                  >
                    <option value="CONTRACT">ระยะสัญญา</option>
                    <option value="SERVICE">จำนวนบริการตรวจเช็ค</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={{ whiteSpace: "nowrap" }}>ใช้คำนวณระยะเวลา</label>
                  <select
                    className="form-select"
                    name="TimeUnit_Moment"
                    value={formData.TimeUnit_Moment || ""}
                    onChange={handleChange}
                    disabled={isServiceType}
                  >
                    <option value="">ไม่ใช้คำนวณ</option>
                    <option value="days">วัน</option>
                    <option value="weeks">สัปดาห์</option>
                    <option value="months">เดือน</option>
                    <option value="years">ปี</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">ลำดับ</label>
                  <input
                    type="number"
                    className="form-control"
                    name="TimeUnit_Sort"
                    value={formData.TimeUnit_Sort ?? ""}
                    onChange={handleChange}
                    min="0"
                    style={{ height: "38px" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">สถานะ</label>
                  <select
                    className="form-select"
                    name="TimeUnit_Status"
                    value={formData.TimeUnit_Status || "Y"}
                    onChange={handleChange}
                    style={{ height: "38px" }}
                  >
                    <option value="Y">ใช้งาน</option>
                    <option value="N">ไม่ใช้งาน</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                ยกเลิก
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={isSaving}
              >
                บันทึก
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: 1055 }}></div>
    </>
  );
};

export default TimeUnitQuickModal;
